import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, Archive, ArchiveRestore, FolderKanban, Loader2, Layers, Sparkles, CheckCircle2, Upload, Pencil, Play, Wand2,
  Coffee, UtensilsCrossed, ShoppingBag, Shirt, Scissors, Stethoscope, Building2,
  Dumbbell, Cpu, Cake, Home, GraduationCap, FileBarChart,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ProductPicker } from "@/components/ProductPicker";
import { DeleteX } from "@/components/DeleteX";

const empty = {
  name: "", business_type: "", description: "", target_audience: "", budget: "",
  has_strategy_ready: false, standalone: false, logo: null, product_image: null, product_name: "", colors: ["#734F96", "#D89134", "#1C4B3E"],
};

const BUSINESS_TEMPLATES = [
  { id: "cafe", icon: Coffee, label: "مقهى / قهوة", business_type: "مقهى متخصص", description: "مقهى يقدم القهوة المختصة والمشروبات الحرفية في أجواء عصرية.", target_audience: "الشباب ومحبو القهوة 20-40 سنة" },
  { id: "restaurant", icon: UtensilsCrossed, label: "مطعم", business_type: "مطعم", description: "مطعم يقدم تجربة طعام مميزة بأطباق متنوعة.", target_audience: "العائلات والأفراد الباحثون عن تجربة طعام" },
  { id: "ecom", icon: ShoppingBag, label: "متجر إلكتروني", business_type: "متجر إلكتروني", description: "متجر إلكتروني لبيع المنتجات عبر الإنترنت مع توصيل سريع.", target_audience: "المتسوقون عبر الإنترنت 18-45 سنة" },
  { id: "fashion", icon: Shirt, label: "أزياء وموضة", business_type: "علامة أزياء", description: "علامة أزياء عصرية تقدم تصاميم مميزة.", target_audience: "محبو الموضة والأناقة 18-40 سنة" },
  { id: "beauty", icon: Scissors, label: "تجميل وعناية", business_type: "صالون تجميل وعناية", description: "صالون يقدم خدمات التجميل والعناية الشخصية.", target_audience: "النساء المهتمات بالعناية والجمال" },
  { id: "clinic", icon: Stethoscope, label: "عيادة طبية", business_type: "عيادة طبية", description: "عيادة تقدم خدمات طبية وتجميلية متخصصة.", target_audience: "الباحثون عن رعاية صحية موثوقة" },
  { id: "realestate", icon: Building2, label: "عقارات", business_type: "شركة عقارية", description: "شركة عقارية لبيع وتأجير العقارات السكنية والتجارية.", target_audience: "المستثمرون والباحثون عن سكن 25-55 سنة" },
  { id: "fitness", icon: Dumbbell, label: "لياقة ورياضة", business_type: "نادٍ رياضي", description: "نادٍ رياضي يقدم برامج لياقة وتدريب شخصي.", target_audience: "المهتمون باللياقة والصحة 18-45 سنة" },
  { id: "tech", icon: Cpu, label: "تقنية وتطبيقات", business_type: "شركة تقنية", description: "شركة تقنية تقدم حلولاً رقمية وتطبيقات مبتكرة.", target_audience: "الشركات والمستخدمون التقنيون" },
  { id: "bakery", icon: Cake, label: "حلويات ومخبوزات", business_type: "محل حلويات ومخبوزات", description: "محل يقدم الحلويات والمخبوزات الطازجة يومياً.", target_audience: "العائلات ومحبو الحلويات" },
  { id: "homeserv", icon: Home, label: "خدمات منزلية", business_type: "شركة خدمات منزلية", description: "شركة تقدم خدمات الصيانة والتنظيف المنزلية.", target_audience: "أصحاب المنازل والعائلات" },
  { id: "education", icon: GraduationCap, label: "تعليم وتدريب", business_type: "مركز تعليم وتدريب", description: "مركز يقدم دورات تدريبية وبرامج تعليمية.", target_audience: "الطلاب والمهنيون الباحثون عن التطوير" },
];

export default function Projects() {
  const { projects, refresh, selectProject } = useProjects();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [track, setTrack] = useState("strategy");
  const [showArchived, setShowArchived] = useState(false);
  const archived = projects.filter((p) => p.status === "archived");
  const shown = projects.filter((p) => (p.status === "archived") === showArchived);

  const contentOnly = user?.plan === "content" || user?.plan === "trial";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const chooseTrack = (t) => {
    setTrack(t);
    if (t === "content") setForm((f) => ({ ...f, standalone: true, has_strategy_ready: false }));
    else setForm((f) => ({ ...f, standalone: false }));
  };

  const openCreate = (forcedTrack) => {
    setEditingId(null);
    setSelectedTemplate(null);
    let t = forcedTrack || (contentOnly ? "content" : "strategy");
    if (contentOnly) t = "content";
    const ready = t === "ready";
    if (ready) t = "strategy";
    setTrack(t);
    setForm(t === "content"
      ? { ...empty, standalone: true }
      : { ...empty, has_strategy_ready: ready });
    setOpen(true);
  };

  useEffect(() => {
    if (location.state?.create) {
      openCreate(location.state.create);
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const openEdit = (p) => {
    setEditingId(p.id);
    setSelectedTemplate(null);
    setTrack(p.standalone ? "content" : "strategy");
    setForm({
      name: p.name || "", business_type: p.business_type || "", description: p.description || "",
      target_audience: p.target_audience || "", budget: p.budget || "",
      has_strategy_ready: !!p.has_strategy_ready, standalone: !!p.standalone,
      logo: p.logo || null, product_image: p.product_image || null, product_name: p.product_name || "",
      colors: p.colors || ["#734F96", "#D89134", "#1C4B3E"],
    });
    setOpen(true);
  };

  const restore = async (id) => {
    try {
      await api.put(`/projects/${id}/archive`, { uploaded: false });
      toast.success("تمت استعادة المشروع إلى لوحة العمل");
      await refresh();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const execute = (p) => {
    selectProject(p.id);
    navigate(p.standalone || p.has_strategy_ready ? "/app/studio" : "/app/strategy");
  };

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("حجم الشعار يجب أن يكون أقل من 2 ميجابايت");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const setColor = (i) => (e) => {
    const colors = [...(form.colors || ["#734F96", "#D89134", "#1C4B3E"])];
    colors[i] = e.target.value;
    setForm({ ...form, colors });
  };

  const create = async () => {
    if (!form.name || !form.business_type) {
      toast.error("الاسم التجاري وطبيعة النشاط مطلوبان");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editingId) {
        await api.put(`/projects/${editingId}`, payload);
        toast.success("تم تحديث المشروع");
        await refresh();
      } else {
        const { data } = await api.post("/projects", payload);
        toast.success("تم إنشاء المشروع بنجاح");
        await refresh();
        selectProject(data.id);
      }
      setOpen(false);
      setForm(empty);
      setSelectedTemplate(null);
      setEditingId(null);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success("تمت أرشفة المشروع — تجده في قسم المؤرشفة مع كل بياناته");
      await refresh();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold font-heading">المشاريع</h1>
          <p className="text-muted-foreground">أنشئ وأدِر علاماتك التجارية.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 text-white gap-2" data-testid="open-create-project" onClick={openCreate}>
              <Plus className="w-4 h-4" /> مشروع جديد
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-xl">{editingId ? "تعديل المشروع" : "إنشاء مشروع جديد"}</DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {editingId ? "عدّل بيانات مشروعك."
                  : track === "content"
                    ? "مشروع محتوى: توليد صور/فيديو/نصوص حسب القناة (باقة المحتوى 249 ريالًا شهرياً — 30 صورة + 8 فيديو مشمولة)."
                    : "مشروع استراتيجية: رحلة كاملة (استراتيجية + أهداف + تنفيذ + قياس + توصيات)."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {!editingId && (
                <div className={`grid gap-3 ${contentOnly ? "grid-cols-1" : "grid-cols-2"}`} data-testid="track-selector">
                  <button type="button" onClick={() => chooseTrack("content")}
                    data-testid="track-content-btn"
                    className={`rounded-xl p-4 border text-start transition-all ${track === "content" ? "border-primary bg-primary/10" : "border-border bg-secondary"}`}>
                    <Wand2 className="w-5 h-5 text-primary mb-2" />
                    <div className="font-bold text-sm">مشروع محتوى</div>
                    <div className="text-xs text-muted-foreground">توليد صور وفيديو ونصوص · باقة 249 ريال/شهر</div>
                  </button>
                  {!contentOnly && (
                    <button type="button" onClick={() => chooseTrack("strategy")}
                      data-testid="track-strategy-btn"
                      className={`rounded-xl p-4 border text-start transition-all ${track === "strategy" ? "border-accent bg-accent/10" : "border-border bg-secondary"}`}>
                      <Sparkles className="w-5 h-5 text-accent mb-2" />
                      <div className="font-bold text-sm">مشروع استراتيجية</div>
                      <div className="text-xs text-muted-foreground">رحلة كاملة · باقة 499 / 1,299</div>
                    </button>
                  )}
                </div>
              )}

              {track === "content" && !editingId && (
                <div className="rounded-xl p-4 border border-primary/30 bg-primary/5 flex items-center gap-3" data-testid="content-notice">
                  <Layers className="w-5 h-5 text-primary shrink-0" />
                  <p className="text-xs">يشمل <b>30 صورة + 8 فيديو</b> شهرياً، وأي محتوى إضافي بالدفع حسب الطلب من محفظتك. صور المشروع تبقى داخله.</p>
                </div>
              )}

              {track === "strategy" && !editingId && (
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setForm({ ...form, has_strategy_ready: false })}
                  data-testid="path-scratch-btn"
                  className={`rounded-xl p-4 border text-start transition-all ${!form.has_strategy_ready ? "border-primary bg-primary/10" : "border-border bg-secondary"}`}>
                  <Sparkles className="w-5 h-5 text-primary mb-2" />
                  <div className="font-bold text-sm">البدء من الصفر</div>
                  <div className="text-xs text-muted-foreground">اختر نشاطك ونولّد الاستراتيجية</div>
                </button>
                <button type="button" onClick={() => setForm({ ...form, has_strategy_ready: true })}
                  data-testid="path-ready-btn"
                  className={`rounded-xl p-4 border text-start transition-all ${form.has_strategy_ready ? "border-accent bg-accent/10" : "border-border bg-secondary"}`}>
                  <Layers className="w-5 h-5 text-accent mb-2" />
                  <div className="font-bold text-sm">لديّ استراتيجية جاهزة</div>
                  <div className="text-xs text-muted-foreground">أدخل الشعار والألوان وابدأ الإنتاج</div>
                </button>
              </div>
              )}

              <div>
                <Label className="mb-2 block">الاسم التجاري *</Label>
                <Input value={form.name} onChange={set("name")} placeholder="مثال: مقهى رؤية" className="bg-secondary border-border" data-testid="project-name-input" />
              </div>

              {!form.has_strategy_ready && (
                <div data-testid="template-section">
                  <Label className="mb-2 block">اختر نشاطك التجاري</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {BUSINESS_TEMPLATES.map((t) => (
                      <button key={t.id} type="button" data-testid={`template-${t.id}`}
                        onClick={() => { setSelectedTemplate(t.id); setForm((f) => ({ ...f, business_type: t.business_type, description: t.description, target_audience: t.target_audience })); }}
                        className={`rounded-xl p-3 border text-center transition-all ${selectedTemplate === t.id ? "border-primary bg-primary/15" : "border-border bg-secondary hover:border-primary/40"}`}>
                        <t.icon className={`w-5 h-5 mx-auto mb-1 ${selectedTemplate === t.id ? "text-primary" : "text-muted-foreground"}`} />
                        <div className="text-[11px] font-semibold leading-tight">{t.label}</div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">يمكنك تعديل التفاصيل المقترحة أدناه بعد الاختيار.</p>
                </div>
              )}

              <div>
                <Label className="mb-2 block">شعار العلامة التجارية <span className="text-muted-foreground text-xs">(اختياري — إن كان لديك شعار مسبق)</span></Label>
                    <div className="flex items-center gap-3">
                      <label className="cursor-pointer flex-1" data-testid="logo-upload-label">
                        <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-upload-input" />
                        <div className="flex items-center gap-2 justify-center rounded-xl border border-dashed border-border bg-secondary py-3 text-sm text-muted-foreground hover:border-primary/40 transition-colors">
                          <Upload className="w-4 h-4" /> {form.logo ? "تغيير الشعار" : "رفع الشعار"}
                        </div>
                      </label>
                      {form.logo && <img src={form.logo} alt="logo" className="w-14 h-14 rounded-xl object-contain border border-border bg-white/5" data-testid="logo-preview" />}
                    </div>
              </div>
              <ProductPicker image={form.product_image} name={form.product_name} testId="project-product"
                onImage={(v) => setForm((f) => ({ ...f, product_image: v || null }))}
                onName={(v) => setForm((f) => ({ ...f, product_name: v }))} />

              {form.has_strategy_ready && (
                <>
                  <div>
                    <Label className="mb-2 block">ألوان الهوية</Label>
                    <div className="flex items-center gap-4">
                      {(form.colors || []).map((c, i) => (
                        <div key={i} className="flex flex-col items-center gap-1">
                          <input type="color" value={c} onChange={setColor(i)} data-testid={`color-input-${i}`}
                            className="w-12 h-12 rounded-lg bg-transparent border border-border cursor-pointer" />
                          <span className="text-[10px] font-mono text-muted-foreground">{c}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label className="mb-2 block">طبيعة النشاط *</Label>
                <Input value={form.business_type} onChange={set("business_type")} placeholder="مثال: مقهى متخصص" className="bg-secondary border-border" data-testid="project-type-input" />
              </div>
              <div>
                <Label className="mb-2 block">وصف المشروع</Label>
                <Textarea value={form.description} onChange={set("description")} placeholder="نبذة عن المشروع ومنتجاته" className="bg-secondary border-border" data-testid="project-desc-input" />
              </div>
              <div>
                <Label className="mb-2 block">الجمهور المستهدف</Label>
                <Input value={form.target_audience} onChange={set("target_audience")} placeholder="مثال: الشباب 20-35 في الرياض" className="bg-secondary border-border" data-testid="project-audience-input" />
              </div>
              <div>
                <Label className="mb-2 block">الميزانية الشهرية (ريال)</Label>
                <Input value={form.budget} onChange={set("budget")} placeholder="مثال: 5000" dir="ltr" className="bg-secondary border-border text-start" data-testid="project-budget-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} className="bg-primary hover:bg-primary/90 text-white w-full gap-2" data-testid="submit-create-project">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {editingId ? "حفظ التعديلات" : "إنشاء المشروع"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2" data-testid="projects-filter">
        <Button size="sm" variant={showArchived ? "outline" : "default"} onClick={() => setShowArchived(false)}
          className={!showArchived ? "bg-primary text-white" : "border-border"} data-testid="projects-tab-active">
          الفعّالة ({projects.length - archived.length})
        </Button>
        <Button size="sm" variant={showArchived ? "default" : "outline"} onClick={() => setShowArchived(true)}
          className={showArchived ? "bg-primary text-white gap-1" : "border-border gap-1"} data-testid="projects-tab-archived">
          <Archive className="w-3.5 h-3.5" /> المؤرشفة ({archived.length})
        </Button>
      </div>

      {shown.length === 0 ? (
        <div className="glass rounded-2xl p-16 text-center border border-border" data-testid="projects-empty">
          <FolderKanban className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{showArchived ? "لا توجد مشاريع مؤرشفة." : "لا توجد مشاريع فعّالة. ابدأ بإنشاء مشروع جديد."}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="glass rounded-2xl p-6 border border-border card-hover" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                {p.logo ? (
                  <img src={p.logo} alt={p.name} className="w-11 h-11 rounded-xl object-contain border border-border bg-white/5" />
                ) : (
                  <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center">
                    <FolderKanban className="w-6 h-6 text-primary" />
                  </div>
                )}
                {p.status === "archived" ? (
                  <div className="flex items-center gap-2">
                    <button onClick={() => restore(p.id)} title="استعادة" className="text-muted-foreground hover:text-primary transition-colors" data-testid={`restore-project-${p.id}`}>
                      <ArchiveRestore className="w-5 h-5" />
                    </button>
                    <DeleteX testId={`purge-project-${p.id}`} title="حذف المشروع نهائياً؟"
                      desc="سيُحذف المشروع المؤرشف وكل محتواه وحملاته وتقاريره نهائياً ولا يمكن استعادته."
                      onConfirm={async () => {
                        try { await api.delete(`/projects/${p.id}/permanent`); toast.success("تم حذف المشروع نهائياً"); await refresh(); }
                        catch (err) { toast.error(formatError(err.response?.data?.detail)); }
                      }} />
                  </div>
                ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button title="أرشفة" className="text-muted-foreground hover:text-accent transition-colors" data-testid={`delete-project-${p.id}`}>
                      <Archive className="w-5 h-5" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-popover border-border" dir="rtl">
                    <AlertDialogHeader>
                      <AlertDialogTitle>أرشفة المشروع؟</AlertDialogTitle>
                      <AlertDialogDescription>سيُنقل المشروع إلى «المؤرشفة» مع الاحتفاظ بكل استراتيجياته ومحتواه وحملاته، ويمكنك استعادته في أي وقت.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                      <AlertDialogCancel>إلغاء</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(p.id)} className="bg-accent text-white" data-testid={`confirm-delete-${p.id}`}>أرشفة</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                )}
              </div>
              <h3 className="text-lg font-bold mb-1">{p.name}</h3>
              <p className="text-sm text-muted-foreground mb-3">{p.business_type}</p>
              {p.target_audience && <p className="text-xs text-muted-foreground mb-3">🎯 {p.target_audience}</p>}
              {p.colors?.length > 0 && (
                <div className="flex gap-1.5 mb-3">
                  {p.colors.map((c, i) => <span key={i} className="w-4 h-4 rounded-full border border-border" style={{ backgroundColor: c }} />)}
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                {p.standalone ? (
                  <span className="text-xs bg-primary/15 text-primary px-2 py-1 rounded-full">مشروع محتوى</span>
                ) : p.has_strategy_ready ? (
                  <span className="text-xs bg-accent/15 text-accent px-2 py-1 rounded-full">استراتيجية خاصة (جاهزة)</span>
                ) : p.has_strategy ? (
                  <span className="text-xs bg-primary/15 text-primary px-2 py-1 rounded-full">مرتبط باستراتيجية رواج الذكية</span>
                ) : (
                  <span className="text-xs bg-secondary px-2 py-1 rounded-full text-muted-foreground">من الصفر · بانتظار الاستراتيجية</span>
                )}
                {p.status === "archived" && <span className="text-xs bg-destructive/15 text-destructive px-2 py-1 rounded-full">مؤرشف</span>}
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" onClick={() => execute(p)} className="flex-1 bg-primary hover:bg-primary/90 text-white gap-1 h-9 text-xs" data-testid={`execute-project-${p.id}`}>
                  <Play className="w-3.5 h-3.5" /> {p.status === "archived" ? "فتح للاطلاع" : "فتح وتنفيذ"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate(`/app/report/${p.id}`)} className="border-border gap-1 h-9 text-xs" data-testid={`project-report-${p.id}`}>
                  <FileBarChart className="w-3.5 h-3.5" /> التقرير
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="border-border gap-1 h-9 text-xs" data-testid={`edit-project-${p.id}`}>
                  <Pencil className="w-3.5 h-3.5" /> تعديل
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
